import {
    accumulatedFilteredUpdate,
    getOrdersGridOptions,
    nonEmptyUpdate,
    overrideHeaderNameWithBrandingConf,
} from './blotter.service.utils';
import { APP_BRANDING_CONFIG_SERVICE, IAppBrandingConfigService } from '../app-branding-config';
import { ColDef, GridOptions } from '@ag-grid-community/core';
import { EBlotterTab } from '../../enums';
import { filter, finalize, scan, shareReplay, takeUntil } from 'rxjs/operators';
import { GRID_CONTEXT_MENU_SERVICE, IGridContextMenuService } from '../grid-context-menu';
import { GRID_SCROLL_SERVICE, IGridScrollService } from '../grid-scroll';
import { IBlotterService } from './blotter.interface';
import { IBlotterUpdate, IOrderReport } from '../../interfaces';
import { inject, injectable } from 'inversify';
import { IOrdersAggregatorService, ORDERS_AGGREGATOR_SERVICE } from '../orders-aggregator';
import { ISessionLifecycle, SESSION_LIFECYCLE_SERVICE } from '../session-lifecycle';
import { merge, Observable, Subject } from 'rxjs';
import { Predicate } from '../../interfaces/functions.interface';

@injectable()
export abstract class BlotterServiceAbstract implements IBlotterService {

    private ordersObs$: Observable<IBlotterUpdate>;

    private readonly destroyed$ = new Subject<boolean>();

    @inject(ORDERS_AGGREGATOR_SERVICE) private readonly ordersAggregator: IOrdersAggregatorService;
    @inject(GRID_SCROLL_SERVICE) private readonly gridScrollSrv: IGridScrollService;
    @inject(GRID_CONTEXT_MENU_SERVICE) gridContextMenuSrv: IGridContextMenuService;
    @inject(SESSION_LIFECYCLE_SERVICE) private readonly sessionLifeCycle: ISessionLifecycle;
    @inject(APP_BRANDING_CONFIG_SERVICE) private readonly brandingSrv: IAppBrandingConfigService;

    abstract columnDefinitions(): Array<ColDef>;

    abstract type: EBlotterTab;

    abstract filter: Predicate<IOrderReport>;

    abstract isRowActionsEnabled: boolean;

    ordersObservable = (): Observable<IBlotterUpdate> =>
        this.getAccumulatedObs().pipe(filter(nonEmptyUpdate));

    gridOptions = async (): Promise<GridOptions> => {
        const brand = await this.brandingSrv.get();
        const columnDefs = this.columnDefinitions()
            .map(overrideHeaderNameWithBrandingConf(brand.blotterHeaderMapping));

        return {
            ...getOrdersGridOptions({
                gridScrollSrv: this.gridScrollSrv,
                isRowActionsEnabled: this.isRowActionsEnabled,
                gridContextMenuSrv: this.gridContextMenuSrv,
                gridType: this.type,
            }),
            columnDefs,
        };
    };

    reset() {
        this.destroyed$.next(true);

        delete this.ordersObs$;
    }

    private readonly getAccumulatedObs = (): Observable<IBlotterUpdate> => {
        const destroyed$ = merge(this.sessionLifeCycle.destroyObservable(), this.destroyed$);

        return this.ordersObs$ ?? (this.ordersObs$ = this.ordersAggregator.ordersObservable()
            .pipe(
                finalize(() => this.ordersObs$ = null),
                takeUntil(destroyed$),
                scan<IBlotterUpdate, IBlotterUpdate>(accumulatedFilteredUpdate(this.filter), { flush: false }),
                shareReplay(1),
            ));
    };
}
